import { Headphones, Plus, Trash2 } from "lucide-react";
import { DiscordChannel, DiscordRole, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import { MultiSelect } from "@/components/MultiSelect";

type VoiceRewardsConfig = GuildConfig["voice_rewards"];
type VoiceRewardTier = VoiceRewardsConfig["tiers"][number];

type Props = {
  config: GuildConfig;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  onChange: (v: VoiceRewardsConfig) => void;
};

function newTier(): VoiceRewardTier {
  return {
    id: `vr_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    enabled: true,
    label: "New voice reward",
    hours: 24,
    role_ids: [],
    channel_ids: [],
  };
}

export function VoiceRewardsTab({ config, channels, roles, onChange }: Props) {
  const cfg = config.voice_rewards ?? {
    enabled: false,
    log_channel_id: "",
    ignored_channel_ids: [],
    tiers: [],
  };
  const channelOptions = channels.map((ch) => ({ id: ch.id, name: ch.name }));
  const roleOptions = roles.map((r) => ({ id: r.id, name: r.name, color: r.color }));

  const update = <K extends keyof VoiceRewardsConfig>(key: K, value: VoiceRewardsConfig[K]) => {
    onChange({ ...cfg, [key]: value });
  };

  const updateTier = (id: string, patch: Partial<VoiceRewardTier>) => {
    onChange({
      ...cfg,
      tiers: cfg.tiers.map((tier) =>
        tier.id === id ? { ...tier, ...patch } : tier,
      ),
    });
  };

  const addTier = () => {
    onChange({ ...cfg, tiers: [...cfg.tiers, newTier()] });
  };

  const removeTier = (id: string) => {
    onChange({ ...cfg, tiers: cfg.tiers.filter((tier) => tier.id !== id) });
  };

  return (
    <Section
      icon={Headphones}
      title="Voice Rewards"
      description="Reward members for time spent in voice channels. Unlock roles or private channels after configurable hour milestones."
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>
          <strong>No slash commands.</strong> When enabled, the bot tracks voice time (excluding ignored channels) and
          grants roles or access when a tier’s hour threshold is reached. Optional <strong>log channel</strong> posts
          when someone earns a reward.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={cfg.enabled}
          onChange={(e) => update("enabled", e.target.checked)}
          className="rounded"
        />
        Enable voice rewards
      </label>

      <Field label="Reward log channel" hint="Optional. Bot posts when a member earns a voice reward.">
        <ChannelPicker
          channels={channels}
          value={cfg.log_channel_id}
          onChange={(v) => update("log_channel_id", v)}
        />
      </Field>

      <Field label="Ignored voice channels" hint="Time in these channels will not count.">
        <MultiSelect
          options={channelOptions}
          value={cfg.ignored_channel_ids}
          onChange={(v) => update("ignored_channel_ids", v)}
          prefix="#"
        />
      </Field>

      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold">Reward tiers</h3>
          <p className="text-sm text-muted-foreground">
            Example: 24h gives a role, 75h unlocks a special channel.
          </p>
        </div>
        <Button type="button" onClick={addTier}>
          <Plus className="h-4 w-4" />
          Add tier
        </Button>
      </div>

      <div className="space-y-3">
        {cfg.tiers.length === 0 && (
          <Card className="p-4 text-sm text-muted-foreground">
            No rewards yet. Add a tier to start rewarding voice activity.
          </Card>
        )}
        {cfg.tiers.map((tier) => (
          <Card key={tier.id} className="p-4 space-y-4 border-border/50">
            <div className="flex items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={tier.enabled}
                  onChange={(e) => updateTier(tier.id, { enabled: e.target.checked })}
                  className="rounded"
                />
                Enabled
              </label>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => removeTier(tier.id)}
              >
                <Trash2 className="h-4 w-4" />
                Remove
              </Button>
            </div>

            <div className="grid md:grid-cols-[1fr_160px] gap-3">
              <Field label="Label">
                <Input
                  value={tier.label}
                  onChange={(e) => updateTier(tier.id, { label: e.target.value })}
                  placeholder="24h voice reward"
                />
              </Field>
              <Field label="Hours required">
                <Input
                  type="number"
                  min={1}
                  value={tier.hours}
                  onChange={(e) =>
                    updateTier(tier.id, { hours: Math.max(1, Number(e.target.value) || 1) })
                  }
                />
              </Field>
            </div>

            <Field label="Roles to give" hint="All selected roles are added when the member reaches this tier.">
              <MultiSelect
                options={roleOptions}
                value={tier.role_ids}
                onChange={(v) => updateTier(tier.id, { role_ids: v })}
                prefix="@"
              />
            </Field>

            <Field label="Channels to unlock" hint="Bot grants this member View Channel permission for selected channels/categories.">
              <MultiSelect
                options={channelOptions}
                value={tier.channel_ids}
                onChange={(v) => updateTier(tier.id, { channel_ids: v })}
                prefix="#"
              />
            </Field>
          </Card>
        ))}
      </div>
    </Section>
  );
}
